// Implements: F-UIX-020, F-UIX-007, F-DSG-009
/**
 * The readable design profiles as the mockup's profile list (`.plist`), and the way into the
 * editor. Pure presentation.
 *
 * One navigation of buttons: the open profile is `aria-current`, and every profile shows four dots of
 * its palette (principal, secundario, fondo, texto; mockup v3 P5) and says where it is active (or that nothing uses it); its version is the editor's to say
 * (owner round 4). The system default goes last and muted. It and any profile the server says the
 * person may not edit (P6-OD-16, through `isEditable`; the default is read-only by design, P6-OD-18)
 * can be opened to be read, marked "Solo lectura": what they open is the container's decision. The
 * colours come from the listing's `palette` (the latest version, read by the server in one query),
 * so no row reads its profile; a colour the listing does not carry shows a neutral dot.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { PaletteRole, ProfileListingView, ProfileSummaryView } from "../../../types/design";
import { isEditable } from "../model/profileModel";
import { joinScopes, scopeLabel } from "../model/scopeModel";
import styles from "./ProfileList.module.css";
import { Swatch } from "./Swatch";

export interface ProfileListProps {
  readonly listing: ProfileListingView;
  /** The server lets the person create a profile in at least one scope (W2l). */
  readonly canCreate: boolean;
  readonly selectedId: string | null;
  readonly onSelect: (profileId: string) => void;
  readonly onNew: () => void;
  /** The screen already says there is nothing yet (a new profile is open in the editor). */
  readonly hideEmpty?: boolean;
}

/** The four dots of a row, in the mockup's order. */
const DOT_ROLES: readonly PaletteRole[] = ["primary", "secondary", "surface", "text"];

export function ProfileList({
  listing,
  canCreate,
  selectedId,
  onSelect,
  onNew,
  hideEmpty = false,
}: ProfileListProps): ReactElement {
  const { t } = useTranslation();
  const profiles = listing.groups.flatMap((group) => group.profiles);
  const hasOwn = profiles.some((profile) => isEditable(profile));
  const words = {
    personal: t("design.scope.personal"),
    team: t("design.scope.team"),
    organization: t("design.scope.organization"),
  };

  const row = (profile: ProfileSummaryView, name: string, muted = false): ReactElement => {
    const active = listing.scopes
      .filter((scope) => scope.active_profile_id === profile.id)
      .map((scope) => scopeLabel(scope, words));
    // The row's words name it (WCAG 2.5.3): its name, where it is active and whether it is read-only, the
    // spaces keeping them apart for assistive technology; flex layout does not render them.
    return (
      <li key={profile.id}>
        <button
          type="button"
          className={styles.profile}
          aria-current={profile.id === selectedId}
          data-muted={muted ? "true" : undefined}
          onClick={() => {
            onSelect(profile.id);
          }}
        >
          <span className={styles.top}>
            <span className={styles.name}>{name}</span>
            <span className={styles.dots}>
              {DOT_ROLES.map((role) => (
                <Swatch key={role} colour={profile.palette[role]} />
              ))}
            </span>
          </span>{" "}
          <span className={styles.bottom}>
            <span className={styles.meta}>
              {active.length === 0 ? t("design.list.unused") : t("design.list.activeIn", { scopes: joinScopes(active) })}
            </span>{" "}
            {isEditable(profile) ? null : <Badge tone="neutral">{t("design.list.readOnly")}</Badge>}
          </span>
        </button>
      </li>
    );
  };

  return (
    <nav className={styles.list} aria-label={t("design.list.title")}>
      <ul className={styles.rows}>
        {profiles.map((profile) => row(profile, profile.name))}
        {row(listing.system_default, t("design.list.systemDefault"), true)}
      </ul>
      {canCreate && hasOwn ? (
        <>
          <div className={styles.separator} />
          <button type="button" className={styles.new} onClick={onNew}>
            <Icon name="plus" size="sm" />
            {t("design.list.new")}
          </button>
        </>
      ) : null}
      {/* Owner round 3: a clear empty state, with the first profile as its primary action. */}
      {hasOwn || hideEmpty ? null : (
        <div className={styles.empty}>
          <h2 className={styles.emptyTitle}>{t("design.list.empty")}</h2>
          <p className={styles.emptyBody}>{t("design.list.emptyBody")}</p>
          {canCreate ? (
            <Button variant="primary" size="sm" onClick={onNew}>
              <Icon name="plus" size="sm" />
              {t("design.list.createFirst")}
            </Button>
          ) : null}
        </div>
      )}
    </nav>
  );
}
